import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBrain,
    faWandMagicSparkles,
    faLink,
    faBolt,
    faRocket,
    faChartColumn
} from '@fortawesome/free-solid-svg-icons';

const Labs = () => {
    const containerRef = useRef(null);
    const [activeSection, setActiveSection] = useState(0);
    const totalSections = 8;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let isScrolling = false;
        let scrollTimeout;

        const handleWheel = (e) => {
            e.preventDefault();

            if (isScrolling) return;

            // Get scroll direction
            const delta = e.deltaY || e.deltaX;

            if (Math.abs(delta) < 10) return; // Ignore tiny movements

            isScrolling = true;

            // Calculate target section
            const sectionWidth = window.innerWidth;
            const currentScroll = container.scrollLeft;
            const currentSection = Math.round(currentScroll / sectionWidth);

            let targetSection;
            if (delta > 0) {
                targetSection = Math.min(currentSection + 1, totalSections - 1);
            } else {
                targetSection = Math.max(currentSection - 1, 0);
            }

            // Smooth scroll to target
            container.scrollTo({
                left: targetSection * sectionWidth,
                behavior: 'smooth'
            });

            setActiveSection(targetSection);

            // Reset scrolling flag after animation
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                isScrolling = false;
            }, 800);
        };

        // Handle scroll position for progress dots
        const handleScroll = () => {
            const sectionWidth = window.innerWidth;
            const currentSection = Math.round(container.scrollLeft / sectionWidth);
            setActiveSection(currentSection);
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, []);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            const container = containerRef.current;
            if (!container) return;

            const sectionWidth = window.innerWidth;

            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                const targetSection = Math.min(activeSection + 1, totalSections - 1);
                container.scrollTo({ left: targetSection * sectionWidth, behavior: 'smooth' });
                setActiveSection(targetSection);
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                const targetSection = Math.max(activeSection - 1, 0);
                container.scrollTo({ left: targetSection * sectionWidth, behavior: 'smooth' });
                setActiveSection(targetSection);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeSection]);

    const beliefs = [
        {
            text: "Employee benefits deserve systems that think, reason, and act for the people who run them.",
            icon: faBrain
        },
        {
            text: "Employee benefits should be designed with intelligence, not guesswork.",
            icon: faWandMagicSparkles
        },
        {
            text: "Data — internal, external, and everything in between — becomes transformative when it's connected and made actionable.",
            icon: faLink
        },
        {
            text: "AI isn't here to replace benefits leaders. It's here to give them leverage.",
            icon: faBolt
        },
        {
            text: "Together, we can modernize the $1.9T benefits engine that keeps American workers healthy.",
            icon: faRocket
        }
    ];

    const goToSection = (index) => {
        const container = containerRef.current;
        if (!container) return;
        container.scrollTo({ left: index * window.innerWidth, behavior: 'smooth' });
        setActiveSection(index);
    };

    return (
        <div className="labs-page">
            {/* Fixed Header */}
            <header className="labs-header">
                <div className="labs-logo">
                    <img src="/avenirlogowhite.png" alt="Avenir" />
                    <span>Avenir <span class="labs-gradient-text-blue">Labs</span></span>
                </div>
                <a href="https://app.youform.com/forms/kheawp2d" className="labs-contact">Get in Touch</a>
            </header>

            {/* Horizontal Scroll Container */}
            <div className="labs-scroll-container" ref={containerRef}>

                {/* Section 1: Hero */}
                <section className="labs-section labs-hero">
                    <div className="labs-blob labs-blob-1"></div>
                    <div className="labs-blob labs-blob-2"></div>
                    <div className="labs-blob labs-blob-3"></div>

                    <div className="labs-hero-content">
                        <p className="labs-tagline">Avenir Labs</p>
                        <h1 className="labs-title">
                            Rebuild <span className="labs-gradient-text-blue">clarity</span>.
                        </h1>
                        <h1 className="labs-title">
                            Redefine <span className="labs-gradient-text-blue">benefits</span>.
                        </h1>
                        <p className="labs-subtitle">Applied research at the intersection of AI, healthcare economics, and workforce science</p>

                        <div className="labs-logos">
                            <img src="/mit.png" alt="MIT" />
                            <img src="/mscopilot.png" alt="Microsoft" />
                            <img src="/harvardchan.png" alt="Harvard" />
                            <img src="/a16z.png" alt="a16z speedrun" />
                        </div>

                        <div className="labs-compliance-badges">
                            <div className="labs-badge">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <path d="M9 12l2 2 4-4" />
                                </svg>
                                <span>SOC 2 Type 2</span>
                            </div>
                            <div className="labs-badge">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <path d="M9 12l2 2 4-4" />
                                </svg>
                                <span>HIPAA</span>
                            </div>
                            <div className="labs-badge" style={{padding: '12px 16px'}}>
                                <span><a href="https://trust.avenir.com.ai" target="_blank" rel="noopener noreferrer">Visit Our Trust Center</a></span>
                            </div>
                        </div>

                        <div className="labs-scroll-hint">
                            <span>Scroll to explore</span>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </div>
                    </div>

                    {/* Floating UI Element */}
                    <div className="labs-float-ui labs-float-1">
                        <div className="labs-ui-card">
                            <div className="labs-ui-header">
                                <span className="labs-ui-dot"></span>
                                <span className="labs-ui-dot"></span>
                                <span className="labs-ui-dot"></span>
                            </div>
                            <div className="labs-ui-content">
                                <div className="labs-ui-metric">
                                    <span className="labs-ui-label">Projected Savings</span>
                                    <span className="labs-ui-value">$2.4M</span>
                                </div>
                                <div className="labs-ui-bar">
                                    <div className="labs-ui-bar-fill" style={{ width: '78%' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 2: We Believe Intro */}
                <section className="labs-section labs-believe-intro">
                    <div className="labs-blob labs-blob-4"></div>
                    <div className="labs-blob labs-blob-5"></div>

                    <div className="labs-believe-content">
                        <p className="labs-eyebrow">Our Philosophy</p>
                        <h2 className="labs-believe-title">We believe…</h2>
                        <p className="labs-hook">The people who keep America's workforce healthy deserve better tools.</p>
                    </div>

                    <div className="labs-float-ui labs-float-2">
                        <div className="labs-ui-card labs-ui-card-dark">
                            <div className="labs-ui-icon">
                                <FontAwesomeIcon icon={faChartColumn} />
                            </div>
                            <span>Plan Simulator</span>
                        </div>
                    </div>
                </section>

                {/* Belief Sections */}
                {beliefs.map((belief, index) => (
                    <section key={index} className={`labs-section labs-belief labs-belief-${index + 1}`}>
                        <div className="labs-blob labs-blob-belief"></div>

                        <div className="labs-belief-content">
                            <div className="labs-belief-icon">
                                <FontAwesomeIcon icon={belief.icon} />
                            </div>
                            <p className="labs-belief-text">{belief.text}</p>
                            <div className="labs-belief-number">0{index + 1}</div>
                        </div>

                        {/* Floating UI elements for each section */}
                        {index === 0 && (
                            <div className="labs-float-ui labs-float-3">
                                <div className="labs-ui-card">
                                    <div className="labs-ui-header">
                                        <span className="labs-ui-dot"></span>
                                        <span className="labs-ui-dot"></span>
                                        <span className="labs-ui-dot"></span>
                                    </div>
                                    <div className="labs-ui-chat">
                                        <div className="labs-ui-chat-msg">Analyze Q3 claims trend</div>
                                        <div className="labs-ui-chat-response">
                                            <span className="labs-ui-typing"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {index === 2 && (
                            <div className="labs-float-ui labs-float-4">
                                <div className="labs-ui-card">
                                    <div className="labs-ui-connections">
                                        <div className="labs-ui-node labs-ui-node-1">TPA</div>
                                        <div className="labs-ui-node labs-ui-node-2">PBM</div>
                                        <div className="labs-ui-node labs-ui-node-3">Stop-Loss</div>
                                        <div className="labs-ui-node labs-ui-node-center">AI</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {index === 4 && (
                            <div className="labs-float-ui labs-float-5">
                                <div className="labs-ui-card">
                                    <div className="labs-ui-metrics-grid">
                                        <div className="labs-ui-metric-small">
                                            <span>Speed</span>
                                            <span className="labs-ui-check">✓</span>
                                        </div>
                                        <div className="labs-ui-metric-small">
                                            <span>Precision</span>
                                            <span className="labs-ui-check">✓</span>
                                        </div>
                                        <div className="labs-ui-metric-small">
                                            <span>Execution</span>
                                            <span className="labs-ui-check">✓</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                ))}

                {/* Final CTA Section */}
                <section className="labs-section labs-cta">
                    <div className="labs-blob labs-blob-6"></div>
                    <div className="labs-blob labs-blob-7"></div>
                    <div className="labs-blob labs-blob-8"></div>

                    <div className="labs-cta-content">
                        <h2 className="labs-cta-title">Believe in this future?</h2>
                        <a href="https://app.youform.com/forms/kheawp2d" className="labs-cta-button">
                            <span>Get in Touch</span>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </a>
                        <p className="labs-cta-tagline">The future of benefits is intelligent.</p>
                    </div>
                </section>

            </div>

            {/* Progress Indicator */}
            <div className="labs-progress">
                <div className="labs-progress-dots">
                    {[...Array(totalSections)].map((_, i) => (
                        <button
                            key={i}
                            className={`labs-progress-dot ${activeSection === i ? 'active' : ''}`}
                            onClick={() => goToSection(i)}
                            aria-label={`Go to section ${i + 1}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Labs;